import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import Hero from "../../components/medical/Hero";
import HighlightedContactSection from "../../components/HighlightedContactSection";
import { getInsightsList, getTopicsWithCounts } from "../../data/insightsData.js";

const TOPIC_LABELS_DE = {
  "Recruiting im Gesundheitswesen": "Recruiting im Gesundheitswesen",
  Direktansprache: "Direktansprache",
  Medizinbranche: "Medizinbranche",
  "Künstliche Intelligenz": "Künstliche Intelligenz",
  Kostenmanagement: "Kostenmanagement",
};

const TYPE_LABELS_DE = {
  ARTICLE: "Artikel",
};

function formatInsightDate(dateValue) {
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

const SORT_OPTIONS = [
  { value: "relevance", label: "Relevanz" },
  { value: "date-desc", label: "Neueste zuerst" },
  { value: "date-asc", label: "Älteste zuerst" },
];

export default function InsightsPage() {
  const location = useLocation();
  const basePath = location.pathname.startsWith("/sales") ? "/sales" : "/medical";
  const insightsList = useMemo(() => getInsightsList(basePath), [basePath]);

  const [selectedTopics, setSelectedTopics] = useState([]);
  const [sort, setSort] = useState("relevance");
  const [sortOpen, setSortOpen] = useState(false);

  const topicsWithCounts = useMemo(() => getTopicsWithCounts(), []);

  const filteredAndSorted = useMemo(() => {
    let list =
      selectedTopics.length === 0
        ? [...insightsList]
        : insightsList.filter((item) => selectedTopics.includes(item.topic));

    if (sort === "date-desc") {
      list = [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (sort === "date-asc") {
      list = [...list].sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    return list;
  }, [selectedTopics, sort, insightsList]);

  const toggleTopic = (topic) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );
  };

  const clearFilters = () => setSelectedTopics([]);

  return (
    <>
      <Hero title="Einblicke" subheading="Aktuelle Einblicke und Fachbeiträge aus der Personalberatung." />
      <div className="min-h-screen bg-white">
      <div className="container-app mx-auto flex flex-col gap-10 py-10 md:flex-row">
        {/* Left: Filters */}
        <aside className="w-full shrink-0 md:w-56 lg:w-64">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              className="flex items-center gap-2 text-gray-900"
              aria-label="Filtermenü"
            >
              <Menu className="h-5 w-5" />
              <span className="text-lg font-semibold">Filter</span>
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="rounded border border-red-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-red-600 outline-none hover:bg-red-50 hover:text-red-700 focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
            >
              Zurücksetzen
            </button>
          </div>

          <div className="mt-6 rounded-2xl border border-gray-200 bg-gray-50/50 p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Themen
            </h3>
            <ul className="mt-4 space-y-3">
              {topicsWithCounts.map(({ topic, count }) => (
                <li key={topic} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id={`topic-${topic}`}
                    checked={selectedTopics.includes(topic)}
                    onChange={() => toggleTopic(topic)}
                    className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                  />
                  <label
                    htmlFor={`topic-${topic}`}
                    className="cursor-pointer text-sm font-medium text-gray-900"
                  >
                    {TOPIC_LABELS_DE[topic] ?? topic}
                  </label>
                  <span className="text-sm text-gray-500">({count})</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Right: Results */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-lg font-semibold text-gray-900">
              {filteredAndSorted.length} Ergebnisse
            </p>
            <div className="relative">
              <button
                type="button"
                onClick={() => setSortOpen((o) => !o)}
                className="flex items-center gap-2 rounded-lg bg-[#121313] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1a1b1c]"
                aria-expanded={sortOpen}
                aria-haspopup="listbox"
              >
                {SORT_OPTIONS.find((o) => o.value === sort)?.label ?? "Relevanz"}
                <svg
                  className={`h-4 w-4 transition-transform ${sortOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {sortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden
                    onClick={() => setSortOpen(false)}
                  />
                  <ul
                    role="listbox"
                    className="absolute right-0 top-full z-20 mt-1 min-w-[180px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <li key={opt.value} role="option" aria-selected={sort === opt.value}>
                        <button
                          type="button"
                          onClick={() => {
                            setSort(opt.value);
                            setSortOpen(false);
                          }}
                          className={`block w-full px-4 py-2 text-left text-sm font-medium transition-colors ${
                            sort === opt.value
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {opt.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>

          <ul className="mt-8 divide-y divide-gray-200">
            {filteredAndSorted.map((item) => (
              <li key={item.id} className="py-8 first:pt-0">
                <article className="flex flex-col gap-3 sm:flex-row sm:gap-6">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {TYPE_LABELS_DE[item.type] ?? item.type} · {formatInsightDate(item.date)}
                    </p>
                    <Link
                      to={item.to}
                      className="mt-2 block text-xl font-bold text-gray-900 decoration-gray-900 decoration-2 underline-offset-4 transition-[text-decoration-color] duration-200 hover:underline"
                    >
                      {item.title}
                    </Link>
                    <p className="mt-3 text-sm leading-relaxed text-gray-700">
                      {item.description}
                    </p>
                  </div>
                  <div className="shrink-0 sm:w-40 sm:min-w-[160px]">
                    <Link to={item.to} className="block overflow-hidden rounded-lg aspect-square">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </Link>
                  </div>
                </article>
              </li>
            ))}
          </ul>

          {filteredAndSorted.length === 0 && (
            <p className="py-12 text-center text-gray-500">Keine Einblicke entsprechen den gewählten Filtern.</p>
          )}
        </div>
      </div>
        <HighlightedContactSection
          heading="Zu Ihrem Thema beraten lassen"
          description="Sie möchten mehr zu einem der Einblicke erfahren oder haben Fragen? Wir freuen uns auf Ihre Nachricht."
          buttonText="Jetzt Kontakt aufnehmen"
          sectionId="section-insights-contact"
        />
      </div>
    </>
  );
}
